import { type AxiosResponse } from 'axios';

// Store
import store from '~/components/betslip/betslip.store';
import {
	// articleTypeHelper,
	// concatenateArticleAuthors,
	getSearchParams,
	getSearchParamsAndHashFragment,
} from '~/components/betslip/util/utils';
// Utils
import { $http, type IHttpOptions } from '~/services/util/$http';
import { CONFIGDATA, NEXT_UP_GAME_TAB } from '~/stores/constants';

// Enums
import { CookieService } from './cookie-actions';
import TrackingActions, {
	CustomerType,
	type ITrackingProps,
	TRACKING_ACTIONS,
	TRACKING_CATEGORIES,
	TRACKING_EVENTS,
} from './tracking-actions';

// Interfaces
// import type IBookmaker from "Interfaces/bookmaker";
// import type IOffer from "Interfaces/offer";

type Offer = any; /* IOffer & IBookmaker; */

interface IClickOutProps {
	bookie: string;
	type: string;
	url: string;
}

export interface ILinkActions {
	claimOffer: (offer: Offer, label: string) => void;
	clickout: (
		url: string,
		bookie: string,
		type: string,
		clickout: boolean,
	) => void;
	dynamicUpdateMarketData: (marketUrl: string) => void;
	isExternalUrl: (initialUrl: string, target?) => boolean;
	navigateTo: (url: string) => void;
	newPage: (newUrl: string, newData: any) => void;
	trackOfferAndClickout: (
		clickout: IClickOutProps,
		trackingObj: ITrackingProps,
	) => void;
	updateHistory: (replaceCurrent: boolean, newUrl: string) => void;
}

class LinkActions implements ILinkActions {
	private trackingActions: TrackingActions = new TrackingActions();
	private cookieService: CookieService = new CookieService();

	protected baseUrl: string;

	private updateClient: (key: string | string[], val: any) => void;
	private replaceServer: (val: any) => void;

	constructor() {
		this.baseUrl = store.getState().server.config.baseUrl;

		this.updateClient = store.getState().updateClient;
		this.replaceServer = store.getState().replaceServer;
	}

	public newPage(newUrl: string, newData: any): void {
		let finalUrl = '';

		const { hashFragmentString, searchParamsString } =
			getSearchParamsAndHashFragment(newUrl);

		// Fix to stop ajax JSON pages caching in Chrome
		const searchParams = getSearchParams(newUrl);
		const searchParamsLen = searchParams?.length;

		if (searchParamsLen) {
			const searchValue = searchParamsLen === 1 ? /\?ajax=1/ : /&ajax=1/;
			newUrl = newUrl.replace(new RegExp(searchValue), '');
		}

		finalUrl = newUrl;

		const baseUrl = newData?.config?.baseUrl || '';
		const currentPath = newData?.config?.currentPath || '';

		if (
			baseUrl &&
			currentPath &&
			newUrl !== baseUrl + currentPath &&
			newUrl.indexOf('myoddschecker') === -1 // on myoddschecker pages, the actual path from the server does not match the real url on the browser
		) {
			finalUrl = baseUrl + currentPath.replace('homepage', '');
		}

		// Reintroduce queryParams + hashFragment
		const finalUrlWithSearchParamsAndHashFragment =
			finalUrl + searchParamsString + hashFragmentString;

		// Page initialisation
		this.updateClient('showAllSportsBottom', false);
		// TODO: clear cache
		// DataStore.clearCache();
		this.updateClient(
			['config', 'currentPath'],
			newData[CONFIGDATA].currentPath,
		);
		this.updateClient(['config', 'showFooter'], false);
		this.updateClient('showAllSports', false);
		this.updateClient('showSearch', false);
		this.updateClient('showMenu', false);
		this.updateClient('navDrawerOpen', false);
		this.updateClient('ajaxError', false);
		this.updateClient(['navigation', 'primary'], false);
		this.updateClient(['navigation', 'secondary'], false);
		this.updateClient('navDrawerOpen', false);
		this.updateClient('betslipOpen', false);

		// Store entire server response
		this.replaceServer(newData);

		// clear next up game cookie if user clicks logo
		if (currentPath === 'homepage') {
			this.cookieService.select(NEXT_UP_GAME_TAB).set();
		}

		this.trackingActions.checkCookies();

		this.updateHistory(false, finalUrlWithSearchParamsAndHashFragment);

		window.scroll(0, 0);

		// ajax page load tracking
		// const { article } = newData;

		// const trackingData: ITrackingProps = {
		//   event: TRACKING_EVENTS.VIRTUAL_PAGE_VIEW,
		//   eventCategory: TRACKING_CATEGORIES.PAGE_VIEW,
		//   eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.VIRTUAL_PAGE_VIEW],
		//   eventLabel: finalUrl,
		//   page: {
		//     title: "pageView",
		//     url: finalUrl,
		//   },
		//   ...(article
		//     ? {
		//         article: articleTypeHelper(article.premium, article.premiumDisplay),
		//       }
		//     : {}),
		//   ...(article?.authors?.length
		//     ? { author: concatenateArticleAuthors(article.authors) }
		//     : {}),
		// };

		// setTimeout(() => {
		//   this.trackingActions.eventTracking(trackingData);
		//   this.trackingActions.optimizeActivate();
		// }, 0);
	}

	public dynamicUpdateMarketData(marketUrl: string): void {
		// TODO: clear cache
		// DataStore.clearCache();
		this.updateClient(['config', 'currentPath'], marketUrl);

		window.scroll(0, 0);
	}

	public claimOffer(offer: Offer, label = ''): void {
		this.trackOfferAndClickout(
			{
				url: offer.bookieCode
					? `clickout/bookie?bookieCode=${offer.bookieCode}`
					: offer.clickout,
				bookie: offer.bookieCode ? offer.bookieCode : offer.displayTitle,
				type: 'offer',
			},
			{
				event: TRACKING_EVENTS.CLAIM_OFFER,
				eventCategory: TRACKING_CATEGORIES.OFFERS,
				eventAction: offer.bookieCode
					? `new${TRACKING_ACTIONS.claimOffer}`
					: `${offer.customerType}${TRACKING_ACTIONS.claimOffer}`,
				eventLabel: label,
				offer: {
					name: offer.name
						? `${offer.name} | default offer`
						: offer.displayTitle,
					customer: offer.bookieCode ? CustomerType.NEW : offer.customerType,
					bookie: offer.bookieCode ? offer.bookieCode : offer.bookmakerCode,
					type: label,
				},
			},
		);
	}

	public trackOfferAndClickout(
		clickout: IClickOutProps,
		trackingObj: ITrackingProps,
	): void {
		this.clickout(clickout.url, clickout.bookie, clickout.type, true);
		this.trackingActions.addObjectToDataLayer(trackingObj);
	}

	public clickout(
		url: string,
		bookie: string,
		type: string,
		clickout = false,
	): void {
		// If WebViewBridge is not available (Web App) then web app should open url in new window.
		if (!window.WebViewBridge) {
			const target = clickout ? bookie : '_self';
			window.open(url, target);

			// if WebViewBridge is available (Wrapper App) then url should be passed to react native.
		} else {
			let formattedUrl = String();

			if (url.indexOf(this.baseUrl) !== -1) {
				formattedUrl = url;
			} else {
				formattedUrl = this.baseUrl + url;
			}
			// @ts-ignore
			window.postMessage(`oc.url.clickout::${formattedUrl}::${type}`);
		}
	}

	public updateHistory(replaceCurrent: boolean, newUrl: string): void {
		const dataStore = store.getState();

		if (location.href === newUrl || replaceCurrent) {
			window.history.replaceState(dataStore, null, newUrl);
		} else {
			window.history.pushState(dataStore, null, newUrl);
		}
	}

	public isExternalUrl(initialUrl: string, target?): boolean {
		if (initialUrl?.startsWith('https://stats')) {
			return true;
		}

		if (initialUrl?.match(/oddschecker\.com\/(us)/)) {
			return false;
		}

		// https://stackoverflow.com/questions/10687099/how-to-test-if-a-url-string-is-absolute-or-relative#19709846
		const r = new RegExp('^(?:[a-z]+:)?//', 'i');

		return r.test(initialUrl) || typeof target !== 'undefined';
	}

	public navigateTo(url: string): void {
		if (this.isExternalUrl(url)) {
			window.open(url, '_blank');
			return;
		}

		const searchParams = getSearchParams(url);
		const searchParamsLen = searchParams?.length || 0;
		const newUrl = searchParamsLen > 1 ? `${url}&ajax=1` : `${url}?ajax=1`;

		const options: IHttpOptions = {
			method: 'GET',
			url: newUrl,
		};

		$http(options)
			.then((res: AxiosResponse) => {
				let data = null;
				if (res.data?.data) {
					data = res.data.data;

					this.updateClient('showSpinner', false);
					this.newPage(url, data);
				}
			})
			.catch(e => {
				// 500 errors go in here so we need to push the new url to history
				// before throwing the error so that our error handling does not
				// replace the previous pages state.
				// this.linkActions.newPage(url, {});

				const status = e?.response?.status;

				if (typeof status === 'number' && status >= 400 && status <= 499) {
					window.open(`${this.baseUrl}404.html`, '_self');
				} else if (
					typeof status === 'number' &&
					status >= 500 &&
					status <= 599
				) {
					window.open(`${this.baseUrl}500.html`, '_self');
				}

				this.updateClient('ajaxError', true);
				// throw new Error('Link: A bad response was received from the server whilst trying to navigate to a new url.');
			})
			.finally(() => {
				this.updateClient('showMenu', false);
				this.updateClient('showAllSports', false);
				this.updateClient('showSpinner', false);
				this.updateClient('showAllSportsBottom', false);
				this.updateClient('navDrawerOpen', false);
				this.updateClient('betslipOpen', false);
			});
	}
}

export default LinkActions;
